
import { useState } from 'react';
import { motion } from "framer-motion";
import { supabase } from "../integrations/supabase/client";

const EmailTester = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailType, setEmailType] = useState<'test' | 'waitlist'>('test');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const sendTestEmail = async () => {
    if (!email) {
      setResult({ success: false, message: 'Please enter an email address' });
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: email,
          subject: emailType === 'waitlist' ? 'Welcome to qwip!' : 'qwip Email Test',
          type: emailType,
          name: name || 'there'
        }
      });

      if (error) throw error;

      if (data.success) {
        setResult({ 
          success: true, 
          message: `Email sent successfully! Message ID: ${data.messageId}` 
        });
      } else {
        setResult({ 
          success: false, 
          message: data.error || 'Failed to send email' 
        });
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      setResult({ 
        success: false, 
        message: error.message || 'Failed to send email' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-[#374151] mb-6">
        Email Testing Tool
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Type
          </label>
          <select
            value={emailType}
            onChange={(e) => setEmailType(e.target.value as 'test' | 'waitlist')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
          >
            <option value="test">Test Email</option>
            <option value="waitlist">Waitlist Welcome Email</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipient Email *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email address to test"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
            required
          />
        </div>

        {emailType === 'waitlist' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name (for waitlist email)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter recipient name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
            />
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={sendTestEmail}
          disabled={isLoading}
          className="w-full bg-[#2563eb] text-white py-2 px-4 rounded-lg font-medium hover:bg-[#1d4ed8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Sending...' : 'Send Test Email'}
        </motion.button>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg ${
              result.success 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {result.message}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EmailTester;
